import { createRouter, createWebHistory } from 'vue-router'
import Dashboard from '@/components/Dashboard.vue'
import Inventory from '@/views/Inventory.vue'
import Picker from '@/views/Picker.vue'
import MyItems from '@/views/MyItems.vue'
import Calendar from '@/components/Calendar.vue'
import UsersAuth0 from '@/views/UsersAuth0.vue'
import RoleManagement from '@/views/RoleManagement.vue'
import SecurityManagement from '@/views/SecurityManagement.vue'
import AdminProducts from '@/views/AdminProducts.vue'
import AdminEquipment from '@/views/AdminEquipment.vue'
import AdminEquipmentDrafts from '@/views/AdminEquipmentDrafts.vue'
import MyEquipOut from '@/views/MyEquipOut.vue'
import OffRoadEdition from '@/views/OffRoadEdition.vue'
import Docs from '@/views/Docs.vue'
import ImportEquipment from '@/views/ImportEquipment.vue'
import Scan from '@/views/Scan.vue'
import { useAuth0Store } from '@/stores/auth0'
import { createLogger } from '@/lib/logger'
import AdminWarehouseAddress from '@/views/AdminWarehouseAddress.vue'
import AdminShipping from '@/views/AdminShipping.vue'
import AdminSpotterCriteria from '@/views/AdminSpotterCriteria.vue'
import Auth0SignIn from '@/views/Auth0SignIn.vue'
import MFA from '@/views/MFA.vue'
import Onboard from '@/views/Onboard.vue'
import Consignees from '@/views/Consignees.vue'

const log = createLogger('Router')

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: '/dashboard', meta: { requiresAuth: true } },
    { path: '/dashboard', component: Dashboard },
    { path: '/dispatch', redirect: '/dashboard', meta: { requiresAuth: true } },
    { path: '/equipment', redirect: '/inventory' },
    { path: '/inventory', component: Inventory },
    { path: '/picker', component: Picker },
    { path: '/scan', component: Scan, meta: { requiresAuth: true, requiredPermission: 'scan_equipment' } },
    { path: '/consignees', component: Consignees, meta: { requiresAuth: true, requiredPermission: 'checkout_equipment' } },
    { path: '/my-items', component: MyItems, meta: { requiresAuth: true } },
    { path: '/calendar', component: Calendar },
    // Permission-gated routes: admin + read-only support roles with view_* permissions
    { path: '/users', component: UsersAuth0, meta: { requiresAuth: true, requiredPermission: 'view_users' } },
    { path: '/roles', component: RoleManagement, meta: { requiresAuth: true, requiredPermission: 'view_roles' } },
    { path: '/security', component: SecurityManagement, meta: { requiresAuth: true, requiredPermission: 'view_audit_logs' } },
    // Admin-only routes: mutation-heavy management tools
    { path: '/admin-products', component: AdminProducts, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/admin-equipment', component: AdminEquipment, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/admin-equipment-drafts', component: AdminEquipmentDrafts, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/admin-equipment/:id', component: AdminEquipment, meta: { requiresAuth: true, requiresAdmin: true }, props: true },
    { path: '/equip-out', component: MyEquipOut },
    { path: '/docs', component: Docs, meta: { requiresAuth: true } },
    { path: '/off-road', component: OffRoadEdition },
    { path: '/admin-warehouse', component: AdminWarehouseAddress, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/admin-shipping', component: AdminShipping, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/admin-spotter-criteria', component: AdminSpotterCriteria, meta: { requiresAuth: true, requiresAdmin: true } },
    { path: '/import', component: ImportEquipment },
    { path: '/signin', component: Auth0SignIn },
    { path: '/mfa', component: MFA, meta: { requiresAuth: true } },
    { path: '/onboard', component: Onboard },
  ],
  scrollBehavior() {
    return { top: 0 }
  },
})

const PUBLIC_ROUTES = new Set(['/signin', '/onboard'])
const MOBILE_ALLOWED_ROUTES = new Set(['/signin', '/scan', '/mfa', '/onboard'])

function isMobileClientRoute(): boolean {
  if (typeof window === 'undefined') return false
  const smallViewport = window.matchMedia('(max-width: 900px)').matches
  const mobileUA = /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent || '')
  const touchViewport =
    window.matchMedia('(max-width: 1024px)').matches && (window.navigator.maxTouchPoints || 0) > 1
  return smallViewport || mobileUA || touchViewport
}

/**
 * Admin-only routes: require admin role (manage_equipment, system_admin, etc.)
 * These are mutation-heavy management tools.
 */
const ADMIN_ONLY_ROUTES = new Set([
  '/admin-products',
  '/admin-equipment',
  '/admin-equipment-drafts',
  '/admin-warehouse',
  '/admin-shipping',
  '/admin-spotter-criteria',
])

/**
 * Routes that require specific view permissions.
 * Read-only support roles can access these with the corresponding view_* permission.
 * Admins have all permissions so they always pass.
 */
const PERMISSION_ROUTES: Record<string, string> = {
  '/consignees': 'checkout_equipment',
  '/scan': 'scan_equipment',
  '/users': 'view_users',
  '/roles': 'view_roles',
  '/security': 'view_audit_logs',
}

// Auth0 authentication guard - STRICT MODE
router.beforeEach(async (to) => {
  const auth0Store = useAuth0Store()
  const isMobileRoute = isMobileClientRoute()

  log.debug('Checking route:', to.path, 'Auth0 state:', {
    loading: auth0Store.isLoading,
    authenticated: auth0Store.isAuthenticated,
    user: auth0Store.user?.email,
  })

  // Keep /signin and /onboard public, but don't leave authenticated users parked there.
  // Never block public routes on Auth0 loading so the sign-in screen stays responsive.
  if (PUBLIC_ROUTES.has(to.path)) {
    if (auth0Store.isLoading) {
      log.debug('Public route allowed while Auth0 initializes:', to.path)
      return true
    }
    if (auth0Store.isAuthenticated) {
      const redirectTarget =
        typeof to.query.redirect === 'string' && to.query.redirect.startsWith('/')
          ? to.query.redirect
          : '/dashboard'
      log.debug('Authenticated user on public route, redirecting to:', redirectTarget)
      return { path: redirectTarget }
    }
    log.debug('Public route allowed:', to.path)
    return true
  }

  if (isMobileRoute && !MOBILE_ALLOWED_ROUTES.has(to.path)) {
    if (auth0Store.isAuthenticated) {
      log.debug('Mobile route restriction redirecting to /scan from:', to.path)
      return { path: '/scan' }
    }
    return { path: '/signin', query: { redirect: '/scan' } }
  }

  // Wait for Auth0 to finish loading - BLOCK protected routes briefly only
  if (auth0Store.isLoading) {
    log.debug('Auth0 still loading, blocking protected route navigation...')

    // Keep this short to avoid long "hung" states on route transitions
    let attempts = 0
    while (auth0Store.isLoading && attempts < 25) {
      await new Promise((resolve) => setTimeout(resolve, 100))
      attempts++
    }

    log.debug('Auth0 loading wait completed after', attempts * 100, 'ms')
  }

  // Also wait for roles to finish loading so permission checks work
  if (auth0Store.roleLoading) {
    let roleAttempts = 0
    while (auth0Store.roleLoading && roleAttempts < 20) {
      await new Promise((resolve) => setTimeout(resolve, 100))
      roleAttempts++
    }
    log.debug('Role loading wait completed after', roleAttempts * 100, 'ms')
  }

  // STRICT: Require authentication for ALL non-public routes
  if (!auth0Store.isAuthenticated) {
    log.debug('NOT AUTHENTICATED - redirecting to signin')
    return { path: '/signin', query: { redirect: to.fullPath } }
  }

  // Require valid user
  if (!auth0Store.user) {
    log.debug('NO USER OBJECT - redirecting to signin')
    return { path: '/signin', query: { redirect: to.fullPath } }
  }

  // Check admin-only routes (equipment catalog, products, warehouse, drafts)
  const basePath = '/' + (to.path.split('/')[1] || '')
  if (ADMIN_ONLY_ROUTES.has(basePath) || ADMIN_ONLY_ROUTES.has(to.path)) {
    if (!auth0Store.isAdmin) {
      log.debug('Not admin, cannot access admin-only route:', to.path)
      return { path: '/dashboard' }
    }
  }

  // Check permission-gated routes (users, roles, security)
  const requiredPermission = PERMISSION_ROUTES[to.path]
  if (requiredPermission) {
    if (!auth0Store.hasPermission(requiredPermission)) {
      log.debug('Missing permission', requiredPermission, 'for route:', to.path)
      return { path: '/dashboard' }
    }
  }

  log.debug('Auth0 authentication passed for user:', auth0Store.user.email)
  return true
})
